/**
 * AlertDialog - A reusable dialog component for alerts and confirmations
 *
 * Built on Material UI's Dialog component, this provides a consistent modal
 * with customizable title, content, and action buttons.
 *
 * Features:
 * - Custom content via children prop (includes default content if none provided)
 * - Customizable button text for primary and secondary actions
 * - Configurable dialog title and ID
 * - Slide-down transition animation
 *
 * @example
 * <AlertDialog
 *   open={isOpen}
 *   onConfirm={handleConfirm}
 *   onCancel={handleCancel}
 *   dialogTitle="Confirm Action"
 *   dialogId="confirm-action"
 * />
 */

import React from 'react';
import PropTypes from 'prop-types';
import {
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Slide,
    CircularProgress
} from '@mui/material';
import { Body1 } from '../../themes/typography';

const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="down" ref={ref} {...props} />;
});

export default function AlertDialog({
    open = false,
    onCancel = () => { },
    onConfirm = () => { },
    secondaryBtnName = 'Cancel',
    primaryBtnName = 'Continue',
    children = <DefaultChildren />,
    dialogId = 'reschedule-confirmation',
    dialogTitle = 'Reschedule Confirmation',
    dialogContentSx = {},
    disabled,
    loading,
    shouldShowSecondaryBtn = true
}) {
    return (
        <React.Fragment>
            <Dialog
                open={open}
                keepMounted
                TransitionComponent={Transition}
                aria-describedby={dialogId}
                sx={dialogStyle}
            >
                <DialogTitle>{dialogTitle}</DialogTitle>
                <DialogContent sx={dialogContentSx}>{children}</DialogContent>
                <DialogActions sx={dialogActionsStyle}>
                    {shouldShowSecondaryBtn && (
                        <Button variant="text" onClick={onCancel} sx={dialogActionsMainBtn}>
                            {secondaryBtnName}
                        </Button>
                    )}
                    
                    <Button
                        variant="main"
                        onClick={onConfirm}
                        sx={dialogActionsMainBtn}
                        disabled={disabled}
                    >
                        {primaryBtnName}{' '}
                        {loading && (
                            <CircularProgress
                                size={20}
                                sx={{ marginLeft: '0.75rem', color: 'var(--primary-color)' }}
                            />
                        )}
                    </Button>
                </DialogActions>
            </Dialog>
        </React.Fragment>
    );
}

// Prop types validations
AlertDialog.propTypes = {
    open: PropTypes.bool,
    children: PropTypes.node,
    onCancel: PropTypes.func,
    onConfirm: PropTypes.func,
    dialogId: PropTypes.string,
    dialogTitle: PropTypes.string,
    primaryBtnName: PropTypes.string,
    secondaryBtnName: PropTypes.string
};

const dialogStyle = {
    '& .MuiPaper-root': {
        background: 'linear-gradient(to top left, #0A1528, #1D3A6E)',
        color: '#f5f5f5',
        borderRadius: '1rem',
        border: '1px solid #CCCCCC',
        minWidth: '600px',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.5rem'
    }
};

const dialogActionsStyle = {
    padding: '1.25rem 1.5rem 1.5rem 1.5rem',
    gap: '0.5rem'
};

const dialogActionsMainBtn = {
    paddingTop: '6px',
    paddingBottom: '6px',
    fontSize: '1.125rem'
};

function DefaultChildren() {
    return (
        <DialogContentText id="alert-dialog-slide-description">
            <ul className="ticket-creation-notes">
                <li>
                    <Body1>Continue by creating tickets for this event to make it live.</Body1>
                </li>
                <li>
                    <Body1>Skip it for now and create tickets later when you are ready.</Body1>
                </li>
            </ul>
        </DialogContentText>
    );
}
